import {
    COLUMN_FORMAT,
    CONTENTS_STATUS_OPTIONS,
    DISPATCH_STATUS_OPTIONS,
    EQUIPMENT_STATUS_OPTIONS,
    EQUIPMENT_TYPE_OPTIONS,
    IDENTITY_STATUS_OPTIONS,
    MESSAGE_STATUS_OPTIONS,
    MR_CONTROL_STATUS_OPTIONS,
    PARTICIPANT_PAY_TYPE_OPTIONS,
    TRAILER_STATUS_OPTIONS,
    TRIP_DETAIL_STATUS_OPTIONS,
    TRIP_HEADER_TRIP_TYPE_OPTIONS,
    TRIP_REQUIRED_OPTIONS,
    TRIP_STATUS_OPTIONS,
} from "../constants";
import { col, dispatchObjectColumns } from "../common/DispatchObjectColumns";

/**
 * Column sets for the bottom pane's grids.
 *
 * Each entry is one Delphi grid from uFMD0000.dfm, in that grid's own column order. `caption` is the
 * field's DisplayLabel from the matching data module, verbatim - where the Delphi defines none (the
 * joined identity columns) the field name is the caption, which is what its grid shows.
 *
 * `hidden: true` marks keys and foreign keys: never rendered, never offered in the column chooser
 * (Architecture.md rule 8). `visible: false` marks columns the Delphi carries but does not show by
 * default - available from the chooser.
 *
 * The four FMDISPATCHOBJECT-shaped tabs (Pick, Del, Empty Container, Hist Ship) take their columns
 * from common/DispatchObjectColumns, which the three top tabs now share - see the note there.
 */

// DATE / DATETIME / INT / DEC / MONEY are the board-wide display types - see COLUMN_FORMAT in
// ../constants for where each one comes from in the legacy persistent fields. Every numeric and date
// column below picks one of them, so no grid states a format of its own.
const { DATE, DATETIME, INT, DEC, MONEY, MESSAGE_DATE } = COLUMN_FORMAT;

/** grdDispatchdetailBottom - FMTRIPDETAIL stops of the selected dispatch object (uFMM0037). */
export const DETAILS_COLUMNS = [
    { dataField: "ROWID", hidden: true },
    { dataField: "R_TRIPHEADER", hidden: true },
    { dataField: "R_FMDISPATCHOBJECT", hidden: true },
    { dataField: "R_ORIGINID", hidden: true },
    { dataField: "R_DESTINATIONID", hidden: true },
    { dataField: "R_LOCATIONID", hidden: true },

    { dataField: "RECORDNUMBER", caption: "Record #" },
    { dataField: "TRIPNO", caption: "Trip #" },
    { dataField: "PUSTOPNO", caption: "PU Stop #", ...INT },
    { dataField: "DELSTOPNO", caption: "Del Stop #", ...INT },
    { dataField: "ORIGINID", caption: "OriginID" },
    { dataField: "DESTINATIONID", caption: "DestinationID" },
    { dataField: "SCHEDULEDPUARRIVALDT", caption: "Scheduled PU Arrival", ...DATETIME },
    { dataField: "SCHEDULEDPUDEPARTUREDT", caption: "Scheduled PU Departure", ...DATETIME },
    { dataField: "SCHEDULEDDELARRIVALDT", caption: "Scheduled Del Arrival", ...DATETIME },
    // Column name misspelled in the schema (DEPATURE); kept faithful to it.
    { dataField: "SCHEDULEDDELDEPATUREDT", caption: "Scheduled Del Departure", ...DATETIME },
    { dataField: "PUORIGIN", caption: "PU Origin" },
    { dataField: "DELDESTINATION", caption: "Del Destination" },

    { dataField: "STATUS", caption: "Status", lookup: TRIP_DETAIL_STATUS_OPTIONS, visible: false },
    { dataField: "TRIPREQUIRED", caption: "Trip Required", lookup: TRIP_REQUIRED_OPTIONS, visible: false },
    { dataField: "ACTUALPUARRIVALDT", caption: "Actual PU Arrival", ...DATETIME, visible: false },
    { dataField: "ACTUALPUDEPARTUREDT", caption: "Actual PU Departure", ...DATETIME, visible: false },
    { dataField: "ACTUALDELARRIVALDT", caption: "Actual Del Arrival", ...DATETIME, visible: false },
    { dataField: "ACTUALDELDEPARTUREDT", caption: "Actual Del Departure", ...DATETIME, visible: false },
    { dataField: "COMMENT", caption: "Comment", visible: false },

    // ---- the rest of uFMM0037's grid columns. Chooser entries, so the default layout is unchanged.
    { dataField: "TrackingNumber", caption: "Tracking #", visible: false },
    { dataField: "ContainerNumber", caption: "Container #", visible: false },
    { dataField: "DispatchGroup", caption: "Group", visible: false },
    { dataField: "Stage", caption: "Stage", visible: false },
    { dataField: "DoPickRoute", caption: "Pickup Route", visible: false },
    { dataField: "DoDelRoute", caption: "Delivery Route", visible: false },
    { dataField: "DispatchingMethod", caption: "Dispatching Method", visible: false },

    // The trip header's own decoration. Captions are uFMM0037's DisplayLabels verbatim - the " Trip"
    // suffix is what tells these apart from the dispatch object's truck and driver elsewhere on the
    // board, so it is load-bearing rather than decoration.
    // The mixed casing is the DTO's, not a slip: TRUCKID, TRAILERID and DRIVERIDENTITYID predate the
    // naming convention on TripDetailDTO and other callers bind them, so only the four added for this
    // grid are PascalCase.
    { dataField: "TripType", caption: "Trip Type", lookup: TRIP_HEADER_TRIP_TYPE_OPTIONS, visible: false },
    { dataField: "TRUCKID", caption: "Truck - Trip", visible: false },
    { dataField: "TRAILERID", caption: "Trailer -1 Trip", visible: false },
    { dataField: "Trailer2Id", caption: "Trailer -2 Trip", visible: false },
    { dataField: "DRIVERIDENTITYID", caption: "Driver - 1 Trip", visible: false },
    { dataField: "Driver2IdentityId", caption: "Driver - 2 Trip", visible: false },
    { dataField: "CarrierId", caption: "Carrier Trip", visible: false },

    // The dispatch object's own actuals - distinct from the stop's, above.
    { dataField: "ActualPuDateDo", caption: "Actual PU Date DO", ...DATETIME, visible: false },
    { dataField: "ActualPuDepartDo", caption: "Actual PU Depart DO", ...DATETIME, visible: false },
    { dataField: "ActualDeliveryDateDo", caption: "Actual Delivery Date DO", ...DATETIME, visible: false },
    { dataField: "ActualDelDepartDo", caption: "Actual Del Depart DO", ...DATETIME, visible: false },

    { dataField: "OriginAddress1", caption: "Origin Address", visible: false },
    { dataField: "OriginCity", caption: "Origin City", visible: false },
    { dataField: "OriginState", caption: "Origin State", visible: false },
    { dataField: "OriginZip", caption: "Origin Zip", visible: false },
    { dataField: "DestAddress1", caption: "Dest Address", visible: false },
    { dataField: "DestCity", caption: "Dest City", visible: false },
    { dataField: "DestState", caption: "Dest State", visible: false },
    { dataField: "DestZip", caption: "Dest Zip", visible: false },

    { dataField: "Reference1Trip", caption: "Reference_1 - Trip", visible: false },
    { dataField: "Reference2Trip", caption: "Reference_2 - Trip", visible: false },
    { dataField: "Reference3Trip", caption: "Reference_3 - Trip", visible: false },
    { dataField: "Reference4Trip", caption: "Reference_4 - Trip", visible: false },
    { dataField: "Reference5Trip", caption: "Reference_5 - Trip", visible: false },

    { dataField: "Reference1Do", caption: "Reference_1 DO", visible: false },
    { dataField: "Reference2Do", caption: "Reference_2 DO", visible: false },
    { dataField: "Reference3Do", caption: "Reference_3 DO", visible: false },
    { dataField: "Reference4Do", caption: "Reference_4 DO", visible: false },
    { dataField: "Reference5Do", caption: "Reference_5 DO", visible: false },
    { dataField: "Reference6Do", caption: "Reference_6 DO", visible: false },
    { dataField: "Reference7Do", caption: "Reference_7 DO", visible: false },
    { dataField: "Reference8Do", caption: "Reference_8 DO", visible: false },
    { dataField: "Reference9Do", caption: "Reference_9 DO", visible: false },
    { dataField: "Reference10Do", caption: "Reference_10 DO", visible: false },

    // FMTRIPDETAIL's own twenty user-defined columns. The "TripDetail" suffix is uFMM0037's, and it
    // is what keeps them apart from the trip header's and the dispatch object's sets on this grid.
    { dataField: "USERDEFINEDCODE_1", caption: "Code-1 TripDetail", visible: false },
    { dataField: "USERDEFINEDCODE_2", caption: "Code-2 TripDetail", visible: false },
    { dataField: "USERDEFINEDCODE_3", caption: "Code-3 TripDetail", visible: false },
    { dataField: "USERDEFINEDCODE_4", caption: "Code-4 TripDetail", visible: false },
    { dataField: "USERDEFINEDCODE_5", caption: "Code-5 TripDetail", visible: false },
    { dataField: "USERDEFINEDREFERENCE_1", caption: "Reference-1 TripDetail", visible: false },
    { dataField: "USERDEFINEDREFERENCE_2", caption: "Reference-2 TripDetail", visible: false },
    { dataField: "USERDEFINEDREFERENCE_3", caption: "Reference-3 TripDetail", visible: false },
    { dataField: "USERDEFINEDREFERENCE_4", caption: "Reference-4 TripDetail", visible: false },
    { dataField: "USERDEFINEDREFERENCE_5", caption: "Reference-5 TripDetail", visible: false },
    { dataField: "USERDEFINEDDATE_1", caption: "Date-1 TripDetail", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_2", caption: "Date-2 TripDetail", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_3", caption: "Date-3 TripDetail", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_4", caption: "Date-4 TripDetail", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_5", caption: "Date-5 TripDetail", ...DATE, visible: false },
    { dataField: "USERDEFINEDQUANTITY_1", caption: "Quantity-1 TripDetail", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_2", caption: "Quantity-2 TripDetail", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_3", caption: "Quantity-3 TripDetail", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_4", caption: "Quantity-4 TripDetail", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_5", caption: "Quantity-5 TripDetail", ...DEC, visible: false },

    // Audit. Already in the projection; they only lacked column definitions.
    { dataField: "CREATIONDATETIME", caption: "Creation Date", ...DATE, visible: false },
    { dataField: "CREATIONUSERID", caption: "Created By", visible: false },
    { dataField: "LASTUPDATEDATETIME", caption: "Last Updated", ...DATE, visible: false },
    { dataField: "LASTUPDATEUSERID", caption: "Last Updated By", visible: false },
    { dataField: "RECORDTYPE", caption: "Record Type", visible: false },
];

/** grdDispatchContents - FMDISPATCHOBJECTCONTENTS of the selected dispatch object (uFMM0084). */
export const CONTENTS_COLUMNS = [
    { dataField: "RowId", hidden: true },
    { dataField: "DispatchObjectRowId", hidden: true },

    { dataField: "RecordNumber", caption: "Record #" },
    // The captions cross over, and that is uFMM0084's own wording: it labels FMCONTENTS.CODE
    // "Contents ID" and FMDISPATCHOBJECTCONTENTS.CONTENTSID "Contents". Reading the property names
    // instead is what put them the other way round.
    { dataField: "Code", caption: "Contents ID" },
    { dataField: "ContentsId", caption: "Contents" },
    { dataField: "Description", caption: "Description" },
    { dataField: "Items", caption: "Items", ...INT },
    { dataField: "NetWeight", caption: "Net Weight", ...DEC },
    { dataField: "ContainerWeight", caption: "Container Weight", ...DEC },
    { dataField: "GrossWeight", caption: "Gross Weight", ...DEC },
    { dataField: "Cubes", caption: "Cubes", ...DEC },
    { dataField: "Pallets", caption: "Pallets", ...INT },

    { dataField: "Status", caption: "Status", lookup: CONTENTS_STATUS_OPTIONS, visible: false },
    { dataField: "Reference", caption: "Reference", visible: false },
    { dataField: "Seal", caption: "Seal", visible: false },
    { dataField: "Comment", caption: "Comment", visible: false },

    // ---- the rest of uFMM0084's grid columns ----
    { dataField: "Type", caption: "Type", visible: false },
    { dataField: "UserDefinedCode1", caption: "Code 1", visible: false },
    { dataField: "UserDefinedCode2", caption: "Code 2", visible: false },
    { dataField: "UserDefinedCode3", caption: "Code 3", visible: false },
    { dataField: "UserDefinedReference1", caption: "Reference 1", visible: false },
    { dataField: "UserDefinedReference2", caption: "Reference 2", visible: false },
    { dataField: "TrackingNumber", caption: "Tracking #", visible: false },
    { dataField: "CreationDateTime", caption: "Creation Date", ...DATE, visible: false },
    { dataField: "CreationUserId", caption: "Created By", visible: false },
    { dataField: "LastUpdateDateTime", caption: "Last Updated", ...DATE, visible: false },
    { dataField: "LastUpdateUserId", caption: "Last Updated By", visible: false },
];

/** grdBottomDispatch (uFMM0056) and grdBottomDelDispatch (uFMM0061). */
export const BOTTOM_DISPATCH_COLUMNS = dispatchObjectColumns([
    col.RecordNumber, col.TrackingNumber, col.ItemsOrdered, col.WeightOrdered, col.CubesOrdered,
    col.PalletsOrdered, col.CommodityCode, col.Origin, col.Destination, col.PayBy,
    col.ReferencePoNumber, col.BillOfLadingNumber, col.ObjectType, col.Status, col.DispatchGroup,
    col.CallInDate, col.Equipment,
]);

/** grdDOContianerBottom (uFMM0027) - leads with the container number. */
export const EMPTY_CONTAINER_COLUMNS = dispatchObjectColumns([
    col.RecordNumber, col.ContainerNumber, col.ItemsOrdered, col.WeightOrdered, col.CubesOrdered,
    col.PalletsOrdered, col.CommodityCode, col.Origin, col.Destination, col.PayBy,
    col.ReferencePoNumber, col.BillOfLadingNumber, col.ObjectType, col.TrackingNumber, col.Status,
    col.DispatchGroup, col.CallInDate, col.Equipment, col.CreateTripSegments,
]);

/** grdBottomHistory (uFMM0046) - leads with the status, as the Delphi history grid does. */
export const HIST_SHIP_COLUMNS = dispatchObjectColumns([
    col.RecordNumber, col.Status, col.TrackingNumber, col.CommodityCode, col.ItemsOrdered,
    col.DispatchGroup, col.CallInDate, col.ConsigneeId, col.CustomerId, col.ShipperId, col.Origin,
    col.Destination, col.Equipment, col.Mileage, col.BillOfLadingNumber, col.WeightOrdered,
    col.CubesOrdered, col.PalletsOrdered, col.ActualPickupDt, col.ActualDeliveryDt,
]);

/** grdBottomTrips (uFMM0038) and grdHistoryTripsBottom (uFMM0045) - the same column set. */
export const TRIPS_COLUMNS = [
    { dataField: "ROWID", hidden: true },
    { dataField: "R_TRUCK", hidden: true },
    { dataField: "R_TRAILER", hidden: true },
    { dataField: "R_DRIVER", hidden: true },

    { dataField: "RECORDNUMBER", caption: "Record #" },
    { dataField: "TRIPNO", caption: "Trip #" },
    { dataField: "TRIPSTATUS", caption: "Trip Status" },
    { dataField: "STATUS", caption: "Status", lookup: TRIP_STATUS_OPTIONS },
    { dataField: "TRIPTYPE", caption: "Trip Type", lookup: TRIP_HEADER_TRIP_TYPE_OPTIONS },
    { dataField: "EQUIPMENT", caption: "Equipment" },
    { dataField: "PLANSTART", caption: "Plan Start", ...DATETIME },
    { dataField: "PLANFINISH", caption: "Plan Finish", ...DATETIME },
    { dataField: "ACTUALSTART", caption: "Actual Start", ...DATETIME },
    { dataField: "ACTUALFINISH", caption: "Actual Finish", ...DATETIME },
    { dataField: "STARTDELAYREASON", caption: "Start Delay Reason" },
    { dataField: "FINISHDELAYREASON", caption: "Finish Delay Reason" },
    { dataField: "PARTICIPANTPAYTYPE", caption: "Participant Pay Type", lookup: PARTICIPANT_PAY_TYPE_OPTIONS },
    { dataField: "ODOMETERSTART", caption: "Odometer Start", ...INT },
    { dataField: "ODOMETEREND", caption: "Odometer End", ...INT },
    { dataField: "MILESLOADED", caption: "Miles Loaded", ...INT },
    { dataField: "MILESUNLOADED", caption: "Miles Unloaded", ...INT },
    { dataField: "TRUCKID", caption: "Truck" },
    { dataField: "TRAILERID", caption: "Trailer" },
    { dataField: "TRAILER2ID", caption: "Trailer2" },
    { dataField: "TRAILER3ID", caption: "Trailer3" },
    { dataField: "DRIVERIDENTITYID", caption: "Driver" },
    { dataField: "DRIVER2IDENTITYID", caption: "Driver2" },

    { dataField: "DISPATCHGROUP", caption: "Group", visible: false },
    { dataField: "STAGE", caption: "Stage", visible: false },

    // ---- carried by TripHeaderDTO, added from uFMM0038 / uFMM0045. Chooser entries only.
    { dataField: "TRUCKREFERENCE", caption: "Truck Ref", visible: false },
    { dataField: "DRIVERREFERENCE", caption: "Driver Ref", visible: false },
    { dataField: "TRAILERREFERENCE", caption: "Trailer Ref", visible: false },
    { dataField: "OSD", caption: "Osd", visible: false },
    { dataField: "USERDEFINEDCODE_1", caption: "Code-1 TripHeader", visible: false },
    { dataField: "USERDEFINEDCODE_2", caption: "Code-2 TripHeader", visible: false },
    { dataField: "USERDEFINEDCODE_3", caption: "Code-3 TripHeader", visible: false },
    { dataField: "USERDEFINEDCODE_4", caption: "Code-4 TripHeader", visible: false },
    { dataField: "USERDEFINEDCODE_5", caption: "Code-5 TripHeader", visible: false },
    { dataField: "USERDEFINEDREFERENCE_1", caption: "Reference-1 TripHeader", visible: false },
    { dataField: "USERDEFINEDREFERENCE_2", caption: "Reference-2 TripHeader", visible: false },
    { dataField: "USERDEFINEDREFERENCE_3", caption: "Reference-3 TripHeader", visible: false },
    { dataField: "USERDEFINEDREFERENCE_4", caption: "Reference-4 TripHeader", visible: false },
    { dataField: "USERDEFINEDREFERENCE_5", caption: "Reference-5 TripHeader", visible: false },
    { dataField: "USERDEFINEDDATE_1", caption: "Date-1 TripHeader", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_2", caption: "Date-2 TripHeader", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_3", caption: "Date-3 TripHeader", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_4", caption: "Date-4 TripHeader", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_5", caption: "Date-5 TripHeader", ...DATE, visible: false },
    { dataField: "USERDEFINEDQUANTITY_1", caption: "Quantity-1 TripHeader", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_2", caption: "Quantity-2 TripHeader", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_3", caption: "Quantity-3 TripHeader", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_4", caption: "Quantity-4 TripHeader", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_5", caption: "Quantity-5 TripHeader", ...DEC, visible: false },
    { dataField: "USERDEFINEDCODE_6", caption: "Code-6 TripHeader", visible: false },
    { dataField: "USERDEFINEDCODE_7", caption: "Code-7 TripHeader", visible: false },
    { dataField: "USERDEFINEDCODE_8", caption: "Code-8 TripHeader", visible: false },
    { dataField: "USERDEFINEDCODE_9", caption: "Code-9 TripHeader", visible: false },
    { dataField: "USERDEFINEDCODE_10", caption: "Code-10 TripHeader", visible: false },
    { dataField: "USERDEFINEDREFERENCE_6", caption: "Reference-6 TripHeader", visible: false },
    { dataField: "USERDEFINEDREFERENCE_7", caption: "Reference-7 TripHeader", visible: false },
    { dataField: "USERDEFINEDREFERENCE_8", caption: "Reference-8 TripHeader", visible: false },
    { dataField: "USERDEFINEDREFERENCE_9", caption: "Reference-9 TripHeader", visible: false },
    { dataField: "USERDEFINEDREFERENCE_10", caption: "Reference-10 TripHeader", visible: false },
    { dataField: "USERDEFINEDDATE_6", caption: "Date-6 TripHeader", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_7", caption: "Date-7 TripHeader", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_8", caption: "Date-8 TripHeader", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_9", caption: "Date-9 TripHeader", ...DATE, visible: false },
    { dataField: "USERDEFINEDDATE_10", caption: "Date-10 TripHeader", ...DATE, visible: false },
    { dataField: "USERDEFINEDQUANTITY_6", caption: "Quantity-6 TripHeader", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_7", caption: "Quantity-7 TripHeader", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_8", caption: "Quantity-8 TripHeader", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_9", caption: "Quantity-9 TripHeader", ...DEC, visible: false },
    { dataField: "USERDEFINEDQUANTITY_10", caption: "Quantity-10 TripHeader", ...DEC, visible: false },
    { dataField: "CREATIONDATETIME", caption: "Creation Date", ...DATE, visible: false },
    { dataField: "CREATIONUSERID", caption: "Created By", visible: false },
    { dataField: "LASTUPDATEDATETIME", caption: "Last Updated", ...DATE, visible: false },
    { dataField: "LASTUPDATEUSERID", caption: "Last Updated By", visible: false },
    { dataField: "PARTICIPANTPAYBASIS", caption: "Part Pay Basis", visible: false },
    { dataField: "PARTICIPANTPAYMETHOD", caption: "Part Pay Method", visible: false },
    { dataField: "PARTICIPANTPAYAMOUNT", caption: "Part Pay Amount", ...MONEY, visible: false },
    { dataField: "ORIGINTO", caption: "Origin To", visible: false },
    { dataField: "DESTINATIONFROM", caption: "Dest From", visible: false },
    { dataField: "DESTINATIONTO", caption: "Dest To", visible: false },
    { dataField: "EXTERNALMIPAYBASIS", caption: "External Mi Pay", visible: false },
    { dataField: "EXTERNALMICALCBASIS", caption: "External Mi Cal", visible: false },

    // ---- uFMM0038 / uFMM0045's six trip-level roll-ups ----
    // Not FMTRIPHEADER columns: each is summed over the trip's stops, through FMTRIPDETAIL into
    // FMDISPATCHOBJECT. The Delphi wraps every one in ISNULL(...,0), so a trip with no stops reads 0.
    { dataField: "ItemsOrdered", caption: "Items", ...INT, visible: false },
    { dataField: "PalletsOrdered", caption: "Pallets", ...INT, visible: false },
    { dataField: "CubesOrdered", caption: "Cubes", ...DEC, visible: false },
    { dataField: "WeightOrdered", caption: "Weight", ...DEC, visible: false },
    { dataField: "OmTotalFreight", caption: "OM Freight", ...DEC, visible: false },
    { dataField: "OmTotalMerc", caption: "OM Merc", ...DEC, visible: false },
];

/**
 * grdBottomTrucks (uFMM0039) and grdBottomTrailers (uFMM0044) - one table, two data modules.
 *
 * Nearly one column set, but not quite: the two modules disagree about three DisplayFormats, and
 * uFMM0044 joins IDTRAILER for three dimensions uFMM0039 has no equivalent of. So the shared base is
 * built once and each tab adds what its own module declares (Architecture.md R1).
 *
 * The three disputed formats are Est Life Hours, Year In Service and Total Life Months. uFMM0039
 * gives all three DisplayFormat ",0"; uFMM0044 gives Est Life Hours ",0.00" and leaves the other two
 * blank, which resolves to ",0.00". One shared INT therefore rendered the Trucks tab right and the
 * Trailers tab wrong - a trailer eight and a half years in service read "9".
 */
const equipmentColumnsBase = [
    { dataField: "RowId", hidden: true },

    { dataField: "RecordNumber", caption: "Record #" },
    { dataField: "EquipmentId", caption: "Equipment ID" },
    { dataField: "Description", caption: "Description" },
    { dataField: "Status", caption: "Status", lookup: EQUIPMENT_STATUS_OPTIONS },
    { dataField: "CurrentCity", caption: "CurrentCity" },
    { dataField: "CurrentState", caption: "CurrentState" },

    { dataField: "EquipmentType", caption: "Equipment Type", lookup: EQUIPMENT_TYPE_OPTIONS, visible: false },

    // ---- the rest of uFMM0039 / uFMM0044's grid columns ----
    { dataField: "CreationDateTime", caption: "Creation Date", ...DATE, visible: false },
    { dataField: "CreationUserId", caption: "Created By", visible: false },
    { dataField: "LastUpdateDateTime", caption: "Last Updated", ...DATE, visible: false },
    { dataField: "LastUpdateUserId", caption: "Last Updated By", visible: false },
    { dataField: "PhysicalAssetGroup", caption: "Assets Group", visible: false },
    { dataField: "Model", caption: "Model", visible: false },
    { dataField: "Year", caption: "Year", visible: false },
    { dataField: "SerialNo", caption: "Serial #", visible: false },
    { dataField: "OwnershipType", caption: "OwnerShip", visible: false },
    { dataField: "DateAquired", caption: "Date Acquired", ...DATE, visible: false },
    { dataField: "InitialCost", caption: "Initial Cost", ...MONEY, visible: false },
    { dataField: "NewUsed", caption: "New Used", visible: false },
    { dataField: "DateInservice", caption: "Date In Service", ...DATE, visible: false },
    { dataField: "TotalCost", caption: "Total Cost", ...MONEY, visible: false },
    { dataField: "EstimatedLifeMonths", caption: "Est Life Months", ...INT, visible: false },
    { dataField: "EstimatedLifeDistance", caption: "Est Life Distance", ...DEC, visible: false },
    // Est Life Hours, Year In Service and Total Life Months are NOT here - the two modules format
    // them differently. See EQUIPMENT_LIFE_COLUMNS below.
    { dataField: "DispositionDate", caption: "Disposition", ...DATE, visible: false },
    { dataField: "AmountReceived", caption: "Amount Received", ...MONEY, visible: false },
    { dataField: "TotalLifeDistance", caption: "Total Life Distance", ...DEC, visible: false },
    { dataField: "TotalLifeHours", caption: "Total Life Hours", ...DEC, visible: false },
    { dataField: "Usage", caption: "Usage", visible: false },
    { dataField: "ChargeOutHourlyRate", caption: "Hourly Rate", ...MONEY, visible: false },
    { dataField: "ChargeOutDailyRate", caption: "Daily Rate", ...MONEY, visible: false },
    { dataField: "ChargeOutWeeklyRate", caption: "Weekly Rate", ...MONEY, visible: false },
    { dataField: "ChargeOutMonthlyRate", caption: "Monthly Rate", ...MONEY, visible: false },
    { dataField: "PeriodicChargeOutBasis", caption: "Charge Out Basis", visible: false },
    { dataField: "MrControlStatus", caption: "MR Status", lookup: MR_CONTROL_STATUS_OPTIONS, visible: false },
    { dataField: "Code1Equipment", caption: "Code 1", visible: false },
    { dataField: "Code2Equipment", caption: "Code 2", visible: false },
    { dataField: "Code3Equipment", caption: "Code 3", visible: false },
    { dataField: "Code4Equipment", caption: "Code 4", visible: false },
    { dataField: "Code5Equipment", caption: "Code 5", visible: false },
    { dataField: "Code6Equipment", caption: "Code 6", visible: false },
    { dataField: "Code7Equipment", caption: "Code 7", visible: false },
    { dataField: "Code8Equipment", caption: "Code 8", visible: false },
    { dataField: "Code9Equipment", caption: "Code 9", visible: false },
    { dataField: "Code10Equipment", caption: "Code 10", visible: false },
    { dataField: "Reference1Equipment", caption: "Reference 1", visible: false },
    { dataField: "Reference2Equipment", caption: "Reference 2", visible: false },
    { dataField: "Reference3Equipment", caption: "Reference 3", visible: false },
    { dataField: "Reference4Equipment", caption: "Reference 4", visible: false },
    { dataField: "Reference5Equipment", caption: "Reference 5", visible: false },
    { dataField: "Reference6Equipment", caption: "Reference 6", visible: false },
    { dataField: "Reference7Equipment", caption: "Reference 7", visible: false },
    { dataField: "Reference8Equipment", caption: "Reference 8", visible: false },
    { dataField: "Reference9Equipment", caption: "Reference 9", visible: false },
    { dataField: "Reference10Equipment", caption: "Reference 10", visible: false },
    { dataField: "Date1Equipment", caption: "Date 1", ...DATE, visible: false },
    { dataField: "Date2Equipment", caption: "Date 2", ...DATE, visible: false },
    { dataField: "Date3Equipment", caption: "Date 3", ...DATE, visible: false },
    { dataField: "Date4Equipment", caption: "Date 4", ...DATE, visible: false },
    { dataField: "Date5Equipment", caption: "Date 5", ...DATE, visible: false },
    { dataField: "Date6Equipment", caption: "Date 6", ...DATE, visible: false },
    { dataField: "Date7Equipment", caption: "Date 7", ...DATE, visible: false },
    { dataField: "Date8Equipment", caption: "Date 8", ...DATE, visible: false },
    { dataField: "Date9Equipment", caption: "Date 9", ...DATE, visible: false },
    { dataField: "Date10Equipment", caption: "Date 10", ...DATE, visible: false },
    { dataField: "Quantity1Equipment", caption: "Quantity 1", ...DEC, visible: false },
    { dataField: "Quantity2Equipment", caption: "Quantity 2", ...DEC, visible: false },
    { dataField: "Quantity3Equipment", caption: "Quantity 3", ...DEC, visible: false },
    { dataField: "Quantity4Equipment", caption: "Quantity 4", ...DEC, visible: false },
    { dataField: "Quantity5Equipment", caption: "Quantity 5", ...DEC, visible: false },
    { dataField: "Quantity6Equipment", caption: "Quantity 6", ...DEC, visible: false },
    { dataField: "Quantity7Equipment", caption: "Quantity 7", ...DEC, visible: false },
    { dataField: "Quantity8Equipment", caption: "Quantity 8", ...DEC, visible: false },
    { dataField: "Quantity9Equipment", caption: "Quantity 9", ...DEC, visible: false },
    { dataField: "Quantity10Equipment", caption: "Quantity 10", ...DEC, visible: false },
    { dataField: "LicensePlateNumber", caption: "License Plate Number", visible: false },
    { dataField: "UserDefinedCode1", caption: "Code-1 Equipment", visible: false },
    { dataField: "UserDefinedCode2", caption: "Code-2 Equipment", visible: false },
    { dataField: "UserDefinedCode3", caption: "Code-3 Equipment", visible: false },
    { dataField: "UserDefinedCode4", caption: "Code-4 Equipment", visible: false },
    { dataField: "UserDefinedCode5", caption: "Code-5 Equipment", visible: false },
    { dataField: "UserDefinedCode6", caption: "Code-6 Equipment", visible: false },
    { dataField: "UserDefinedCode7", caption: "Code-7 Equipment", visible: false },
    { dataField: "UserDefinedCode8", caption: "Code-8 Equipment", visible: false },
    { dataField: "UserDefinedCode9", caption: "Code-9 Equipment", visible: false },
    { dataField: "UserDefinedCode10", caption: "Code-10 Equipment", visible: false },
    { dataField: "UserDefinedReference1", caption: "Reference-1 Equipment", visible: false },
    { dataField: "UserDefinedReference2", caption: "Reference-2 Equipment", visible: false },
    { dataField: "UserDefinedReference3", caption: "Reference-3 Equipment", visible: false },
    { dataField: "UserDefinedReference4", caption: "Reference-4 Equipment", visible: false },
    { dataField: "UserDefinedReference5", caption: "Reference-5 Equipment", visible: false },
    { dataField: "UserDefinedReference6", caption: "Reference-6 Equipment", visible: false },
    { dataField: "UserDefinedReference7", caption: "Reference-7 Equipment", visible: false },
    { dataField: "UserDefinedReference8", caption: "Reference-8 Equipment", visible: false },
    { dataField: "UserDefinedReference9", caption: "Reference-9 Equipment", visible: false },
    { dataField: "UserDefinedReference10", caption: "Reference-10 Equipment", visible: false },
    { dataField: "UserDefinedDate1", caption: "Date-1 Equipment", ...DATE, visible: false },
    { dataField: "UserDefinedDate2", caption: "Date-2 Equipment", ...DATE, visible: false },
    { dataField: "UserDefinedDate3", caption: "Date-3 Equipment", ...DATE, visible: false },
    { dataField: "UserDefinedDate4", caption: "Date-4 Equipment", ...DATE, visible: false },
    { dataField: "UserDefinedDate5", caption: "Date-5 Equipment", ...DATE, visible: false },
    { dataField: "UserDefinedDate6", caption: "Date-6 Equipment", ...DATE, visible: false },
    { dataField: "UserDefinedDate7", caption: "Date-7 Equipment", ...DATE, visible: false },
    { dataField: "UserDefinedDate8", caption: "Date-8 Equipment", ...DATE, visible: false },
    { dataField: "UserDefinedDate9", caption: "Date-9 Equipment", ...DATE, visible: false },
    { dataField: "UserDefinedDate10", caption: "Date-10 Equipment", ...DATE, visible: false },
    { dataField: "UserDefinedQuantity1", caption: "Quantity-1 Equipment", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity2", caption: "Quantity-2 Equipment", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity3", caption: "Quantity-3 Equipment", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity4", caption: "Quantity-4 Equipment", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity5", caption: "Quantity-5 Equipment", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity6", caption: "Quantity-6 Equipment", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity7", caption: "Quantity-7 Equipment", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity8", caption: "Quantity-8 Equipment", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity9", caption: "Quantity-9 Equipment", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity10", caption: "Quantity-10 Equipment", ...DEC, visible: false },
    { dataField: "CurrentLocationCode", caption: "Current Location Cd", visible: false },
];

/**
 * The three life columns whose DisplayFormat the two equipment modules disagree about.
 *
 * @param {object} format COLUMN_FORMAT.INT for uFMM0039 (Trucks), COLUMN_FORMAT.DEC for uFMM0044
 *                        (Trailers), which declares Est Life Hours ",0.00" and leaves the other two
 *                        blank - and a blank DisplayFormat on a TAbFloatField resolves to ",0.00".
 */
const equipmentLifeColumns = (format) => [
    { dataField: "EstimatedLifeHours", caption: "Est Life Hours", ...format, visible: false },
    { dataField: "YearsInService", caption: "Year In Service", ...format, visible: false },
    { dataField: "TotalLifeMonths", caption: "Total Life Months", ...format, visible: false },
];

/**
 * grdBottomTrucks (uFMM0039). Tare comes off IDPOWER, which only a power unit has.
 */
export const TRUCKS_COLUMNS = [
    ...equipmentColumnsBase,
    ...equipmentLifeColumns(INT),
    { dataField: "Tare", caption: "Tare", ...DEC, visible: false },
];

/**
 * grdBottomTrailers (uFMM0044). Tare and the three dimensions come off IDTRAILER; all four are
 * TAbFloatField with DisplayFormat ",0.00".
 */
export const TRAILERS_COLUMNS = [
    ...equipmentColumnsBase,
    ...equipmentLifeColumns(DEC),
    { dataField: "Tare", caption: "Tare", ...DEC, visible: false },
    { dataField: "Width", caption: "Width", ...DEC, visible: false },
    { dataField: "Length", caption: "Length", ...DEC, visible: false },
    { dataField: "Height", caption: "Height", ...DEC, visible: false },
];

/** grdBottomDrivers (uFMM0040). */
export const DRIVERS_COLUMNS = [
    { dataField: "RowId", hidden: true },

    { dataField: "RecordNumber", caption: "Record #" },
    // uFMM0040's DisplayLabels. City, State and Zip carry none there, so the field name is the
    // caption - which is why those three read differently from the ones around them.
    { dataField: "IdentityId", caption: "IdentityID" },
    { dataField: "Name", caption: "Name" },
    { dataField: "AddressName", caption: "Address Name" },
    { dataField: "Address1", caption: "Address - 1" },
    { dataField: "Address2", caption: "Address - 2" },
    { dataField: "City", caption: "City" },
    { dataField: "State", caption: "State" },
    { dataField: "Zip", caption: "Zip" },
    { dataField: "Telephone1", caption: "Telephone - 1" },
    { dataField: "CurrentCity", caption: "CurrentCity" },
    { dataField: "CurrentState", caption: "CurrentState" },

    { dataField: "Country", caption: "Country", visible: false },
    { dataField: "Fax1", caption: "Fax - 1", visible: false },
    { dataField: "CellPhone1", caption: "Cell Phone - 1", visible: false },
    { dataField: "Pager1", caption: "Pager - 1", visible: false },
    { dataField: "Status", caption: "Status", lookup: IDENTITY_STATUS_OPTIONS, visible: false },
    // ---- the rest of this grid's legacy columns ----
    { dataField: "CreationDateTime", caption: "Creation Date", ...DATE, visible: false },
    { dataField: "CreationUserId", caption: "Created By", visible: false },
    { dataField: "LastUpdateDateTime", caption: "Last Updated", ...DATE, visible: false },
    { dataField: "LastUpdateUserId", caption: "Last Updated By", visible: false },
    { dataField: "IdType", caption: "ID Type", visible: false },
    { dataField: "Telephone2", caption: "Telephone - 2", visible: false },
    { dataField: "Fax2", caption: "Fax - 2", visible: false },
    { dataField: "Modem1", caption: "Modem - 1", visible: false },
    { dataField: "Modem2", caption: "Modem - 2", visible: false },
    { dataField: "CellPhone2", caption: "Cell Phone - 2", visible: false },
    { dataField: "Pager2", caption: "Pager - 2", visible: false },
    { dataField: "EmailAddress1", caption: "EmailAddress_1", visible: false },
    { dataField: "EmailAddress2", caption: "EmailAddress_2", visible: false },
    { dataField: "WebSiteAddress1", caption: "Web - 1", visible: false },
    { dataField: "WebSiteAddress2", caption: "Web - 2", visible: false },
    { dataField: "Latitude", caption: "Latitude", ...DEC, visible: false },
    { dataField: "Longitude", caption: "Longitude", ...DEC, visible: false },
    { dataField: "IdGroup", caption: "ID Group", visible: false },
    { dataField: "ContactId", caption: "CONTACTID", visible: false },
    { dataField: "UserDefinedCd1Carrier", caption: "UserdefinedCD1_Carrier", visible: false },
    { dataField: "UserDefinedCd2Carrier", caption: "UserdefinedCD2_Carrier", visible: false },
    { dataField: "UserDefinedCd3Carrier", caption: "UserdefinedCD3_Carrier", visible: false },
    { dataField: "UserDefinedCd4Carrier", caption: "UserdefinedCD4_Carrier", visible: false },
    { dataField: "UserDefinedCd5Carrier", caption: "UserdefinedCD5_Carrier", visible: false },
    { dataField: "UserDefinedCd6Carrier", caption: "UserdefinedCD6_Carrier", visible: false },
    { dataField: "UserDefinedCd7Carrier", caption: "UserdefinedCD7_Carrier", visible: false },
    { dataField: "UserDefinedCd8Carrier", caption: "UserdefinedCD8_Carrier", visible: false },
    { dataField: "UserDefinedCd9Carrier", caption: "UserdefinedCD9_Carrier", visible: false },
    { dataField: "UserDefinedCd10Carrier", caption: "UserdefinedCD10_Carrier", visible: false },
    { dataField: "UserDefinedDt1Carrier", caption: "UserDefinedDt1_Carrier", ...DATE, visible: false },
    { dataField: "UserDefinedDt2Carrier", caption: "UserDefinedDt2_Carrier", ...DATE, visible: false },
    { dataField: "UserDefinedDt3Carrier", caption: "UserDefinedDt3_Carrier", ...DATE, visible: false },
    { dataField: "UserDefinedDt4Carrier", caption: "UserDefinedDt4_Carrier", ...DATE, visible: false },
    { dataField: "UserDefinedDt5Carrier", caption: "UserDefinedDt5_Carrier", ...DATE, visible: false },
    { dataField: "UserDefinedDt6Carrier", caption: "UserDefinedDt6_Carrier", ...DATE, visible: false },
    { dataField: "UserDefinedDt7Carrier", caption: "UserDefinedDt7_Carrier", ...DATE, visible: false },
    { dataField: "UserDefinedDt8Carrier", caption: "UserDefinedDt8_Carrier", ...DATE, visible: false },
    { dataField: "UserDefinedDt9Carrier", caption: "UserDefinedDt9_Carrier", ...DATE, visible: false },
    { dataField: "UserDefinedDt10Carrier", caption: "UserDefinedDt10_Carrier", ...DATE, visible: false },
    { dataField: "UserDefinedQty1Carrier", caption: "UserDefinedQty1_Carrier", ...DEC, visible: false },
    { dataField: "UserDefinedQty2Carrier", caption: "UserDefinedQty2_Carrier", ...DEC, visible: false },
    { dataField: "UserDefinedQty3Carrier", caption: "UserDefinedQty3_Carrier", ...DEC, visible: false },
    { dataField: "UserDefinedQty4Carrier", caption: "UserDefinedQty4_Carrier", ...DEC, visible: false },
    { dataField: "UserDefinedQty5Carrier", caption: "UserDefinedQty5_Carrier", ...DEC, visible: false },
    { dataField: "UserDefinedQty6Carrier", caption: "UserDefinedQty6_Carrier", ...DEC, visible: false },
    { dataField: "UserDefinedQty7Carrier", caption: "UserDefinedQty7_Carrier", ...DEC, visible: false },
    { dataField: "UserDefinedQty8Carrier", caption: "UserDefinedQty8_Carrier", ...DEC, visible: false },
    { dataField: "UserDefinedQty9Carrier", caption: "UserDefinedQty9_Carrier", ...DEC, visible: false },
    { dataField: "UserDefinedQty10Carrier", caption: "UserDefinedQty10_Carrier", ...DEC, visible: false },
    { dataField: "UserDefinedRef1Carrier", caption: "UserDefinedRef1_Carrier", visible: false },
    { dataField: "UserDefinedRef2Carrier", caption: "UserDefinedRef2_Carrier", visible: false },
    { dataField: "UserDefinedRef3Carrier", caption: "UserDefinedRef3_Carrier", visible: false },
    { dataField: "UserDefinedRef4Carrier", caption: "UserDefinedRef4_Carrier", visible: false },
    { dataField: "UserDefinedRef5Carrier", caption: "UserDefinedRef5_Carrier", visible: false },
    { dataField: "UserDefinedRef6Carrier", caption: "UserDefinedRef6_Carrier", visible: false },
    { dataField: "UserDefinedRef7Carrier", caption: "UserDefinedRef7_Carrier", visible: false },
    { dataField: "UserDefinedRef8Carrier", caption: "UserDefinedRef8_Carrier", visible: false },
    { dataField: "UserDefinedRef9Carrier", caption: "UserDefinedRef9_Carrier", visible: false },
    { dataField: "UserDefinedRef10Carrier", caption: "UserDefinedRef10_Carrier", visible: false },
    { dataField: "UserDefinedCd1Driver", caption: "UserdefinedCD1_Driver", visible: false },
    { dataField: "UserDefinedCd2Driver", caption: "UserdefinedCD2_Driver", visible: false },
    { dataField: "UserDefinedCd3Driver", caption: "UserdefinedCD3_Driver", visible: false },
    { dataField: "UserDefinedCd4Driver", caption: "UserdefinedCD4_Driver", visible: false },
    { dataField: "UserDefinedCd5Driver", caption: "UserdefinedCD5_Driver", visible: false },
    { dataField: "UserDefinedCd6Driver", caption: "UserdefinedCD6_Driver", visible: false },
    { dataField: "UserDefinedCd7Driver", caption: "UserdefinedCD7_Driver", visible: false },
    { dataField: "UserDefinedCd8Driver", caption: "UserdefinedCD8_Driver", visible: false },
    { dataField: "UserDefinedCd9Driver", caption: "UserdefinedCD9_Driver", visible: false },
    { dataField: "UserDefinedCd10Driver", caption: "UserdefinedCD10_Driver", visible: false },
    { dataField: "UserDefinedDt1Driver", caption: "UserDefinedDt1_Driver", ...DATE, visible: false },
    { dataField: "UserDefinedDt2Driver", caption: "UserDefinedDt2_Driver", ...DATE, visible: false },
    { dataField: "UserDefinedDt3Driver", caption: "UserDefinedDt3_Driver", ...DATE, visible: false },
    { dataField: "UserDefinedDt4Driver", caption: "UserDefinedDt4_Driver", ...DATE, visible: false },
    { dataField: "UserDefinedDt5Driver", caption: "UserDefinedDt5_Driver", ...DATE, visible: false },
    { dataField: "UserDefinedDt6Driver", caption: "UserDefinedDt6_Driver", ...DATE, visible: false },
    { dataField: "UserDefinedDt7Driver", caption: "UserDefinedDt7_Driver", ...DATE, visible: false },
    { dataField: "UserDefinedDt8Driver", caption: "UserDefinedDt8_Driver", ...DATE, visible: false },
    { dataField: "UserDefinedDt9Driver", caption: "UserDefinedDt9_Driver", ...DATE, visible: false },
    { dataField: "UserDefinedDt10Driver", caption: "UserDefinedDt10_Driver", ...DATE, visible: false },
    { dataField: "UserDefinedQty1Driver", caption: "UserDefinedQty1_Driver", ...DEC, visible: false },
    { dataField: "UserDefinedQty2Driver", caption: "UserDefinedQty2_Driver", ...DEC, visible: false },
    { dataField: "UserDefinedQty3Driver", caption: "UserDefinedQty3_Driver", ...DEC, visible: false },
    { dataField: "UserDefinedQty4Driver", caption: "UserDefinedQty4_Driver", ...DEC, visible: false },
    { dataField: "UserDefinedQty5Driver", caption: "UserDefinedQty5_Driver", ...DEC, visible: false },
    { dataField: "UserDefinedQty6Driver", caption: "UserDefinedQty6_Driver", ...DEC, visible: false },
    { dataField: "UserDefinedQty7Driver", caption: "UserDefinedQty7_Driver", ...DEC, visible: false },
    { dataField: "UserDefinedQty8Driver", caption: "UserDefinedQty8_Driver", ...DEC, visible: false },
    { dataField: "UserDefinedQty9Driver", caption: "UserDefinedQty9_Driver", ...DEC, visible: false },
    { dataField: "UserDefinedQty10Driver", caption: "UserDefinedQty10_Driver", ...DEC, visible: false },
    { dataField: "UserDefinedRef1Driver", caption: "UserDefinedRef1_Driver", visible: false },
    { dataField: "UserDefinedRef2Driver", caption: "UserDefinedRef2_Driver", visible: false },
    { dataField: "UserDefinedRef3Driver", caption: "UserDefinedRef3_Driver", visible: false },
    { dataField: "UserDefinedRef4Driver", caption: "UserDefinedRef4_Driver", visible: false },
    { dataField: "UserDefinedRef5Driver", caption: "UserDefinedRef5_Driver", visible: false },
    { dataField: "UserDefinedRef6Driver", caption: "UserDefinedRef6_Driver", visible: false },
    { dataField: "UserDefinedRef7Driver", caption: "UserDefinedRef7_Driver", visible: false },
    { dataField: "UserDefinedRef8Driver", caption: "UserDefinedRef8_Driver", visible: false },
    { dataField: "UserDefinedRef9Driver", caption: "UserDefinedRef9_Driver", visible: false },
    { dataField: "UserDefinedRef10Driver", caption: "UserDefinedRef10_Driver", visible: false },
    { dataField: "ContactName", caption: "Contact Name", visible: false },
    { dataField: "AlternateName", caption: "Alternate Name", visible: false },
    { dataField: "CarrierStatus", caption: "Carrier Status", lookup: IDENTITY_STATUS_OPTIONS, visible: false },
    { dataField: "CarrierInsAmt", caption: "Carrier Ins Amt", ...MONEY, visible: false },
    { dataField: "CarrierInsExpiration", caption: "Carrier Ins Expiration", ...DATE, visible: false },
    { dataField: "CarrierScac", caption: "Carrier SCAC", visible: false },
];

/** grdBottomShippers (uIDM0092). */
export const SHIPPERS_COLUMNS = [
    { dataField: "RowId", hidden: true },

    { dataField: "RecordNumber", caption: "Record #" },
    // uIDM0092's DisplayLabels. As on Drivers, City/State/Zip carry none, so they read as the field
    // name; note this module spells the identity label "Identity ID" where uFMM0040 says "IdentityID".
    { dataField: "IdentityId", caption: "Identity ID" },
    { dataField: "Name", caption: "Name" },
    { dataField: "Address1", caption: "Address - 1" },
    { dataField: "Address2", caption: "Address - 2" },
    { dataField: "City", caption: "City" },
    { dataField: "State", caption: "State" },
    { dataField: "Zip", caption: "Zip" },
    { dataField: "Country", caption: "Country" },
    { dataField: "Telephone1", caption: "Telephone - 1" },
    { dataField: "Fax1", caption: "Fax - 1" },
    { dataField: "CellPhone1", caption: "Cell Phone - 1" },
    { dataField: "Pager1", caption: "Pager - 1" },

    { dataField: "AddressName", caption: "Address Name", visible: false },
    { dataField: "Status", caption: "Status", lookup: IDENTITY_STATUS_OPTIONS, visible: false },
    { dataField: "CurrentCity", caption: "CurrentCity", visible: false },
    { dataField: "CurrentState", caption: "CurrentState", visible: false },
    // ---- the rest of this grid's legacy columns ----
    { dataField: "CreationDateTime", caption: "Creation Date", ...DATE, visible: false },
    { dataField: "CreationUserId", caption: "Created By", visible: false },
    { dataField: "LastUpdateDateTime", caption: "Last Updated", ...DATE, visible: false },
    { dataField: "LastUpdateUserId", caption: "Last Updated By", visible: false },
    { dataField: "IdType", caption: "ID Type", visible: false },
    { dataField: "Telephone2", caption: "Telephone - 2", visible: false },
    { dataField: "Fax2", caption: "Fax - 2", visible: false },
    { dataField: "Modem1", caption: "Modem - 1", visible: false },
    { dataField: "Modem2", caption: "Modem - 2", visible: false },
    { dataField: "CellPhone2", caption: "Cell Phone - 2", visible: false },
    { dataField: "Pager2", caption: "Pager - 2", visible: false },
    { dataField: "EmailAddress1", caption: "EmailAddress_1", visible: false },
    { dataField: "EmailAddress2", caption: "EmailAddress_2", visible: false },
    { dataField: "WebSiteAddress1", caption: "Web - 1", visible: false },
    { dataField: "WebSiteAddress2", caption: "Web - 2", visible: false },
    { dataField: "ScacCode", caption: "SCAC Code", visible: false },
    { dataField: "Location", caption: "Location", visible: false },
    { dataField: "IdQualifier", caption: "ID Qualifier", visible: false },
    { dataField: "IdCode", caption: "ID Code", visible: false },
    { dataField: "LocationQualifier", caption: "Locn Qualifier", visible: false },
    { dataField: "LocationIdentifier", caption: "Locn Code", visible: false },
    { dataField: "UserDefinedCode1", caption: "Code-1", visible: false },
    { dataField: "UserDefinedCode2", caption: "Code-2", visible: false },
    { dataField: "UserDefinedCode3", caption: "Code-3", visible: false },
    { dataField: "UserDefinedCode4", caption: "Code-4", visible: false },
    { dataField: "UserDefinedCode5", caption: "Code-5", visible: false },
    { dataField: "UserDefinedReference1", caption: "Reference-1", visible: false },
    { dataField: "UserDefinedReference2", caption: "Reference-2", visible: false },
    { dataField: "UserDefinedReference3", caption: "Reference-3", visible: false },
    { dataField: "UserDefinedReference4", caption: "Reference-4", visible: false },
    { dataField: "UserDefinedReference5", caption: "Reference-5", visible: false },
    { dataField: "UserDefinedDate1", caption: "Date-1", ...DATE, visible: false },
    { dataField: "UserDefinedDate2", caption: "Date-2", ...DATE, visible: false },
    { dataField: "UserDefinedDate3", caption: "Date-3", ...DATE, visible: false },
    { dataField: "UserDefinedDate4", caption: "Date-4", ...DATE, visible: false },
    { dataField: "UserDefinedDate5", caption: "Date-5", ...DATE, visible: false },
    { dataField: "UserDefinedQuantity1", caption: "Quantity-1", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity2", caption: "Quantity-2", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity3", caption: "Quantity-3", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity4", caption: "Quantity-4", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity5", caption: "Quantity-5", ...DEC, visible: false },
    { dataField: "AddressRowId", caption: "AddressROWID", visible: false },
];

/** grdBottomMessages (uFMM0042). */
export const MESSAGES_COLUMNS = [
    { dataField: "RowId", hidden: true },

    { dataField: "RecordNumber", caption: "Record #" },
    { dataField: "TripNo", caption: "TripNo" },
    { dataField: "Status", caption: "Status", lookup: MESSAGE_STATUS_OPTIONS },
    // The one datetime column on the whole board with an explicit DisplayFormat of its own:
    // uFMM0042 declares "mm/dd/yyyy hh:mm" - zero-padded, 24-hour, no seconds - where every other
    // moment on the board takes the framework default. See MESSAGE_DATE in ../constants.
    { dataField: "MessageDate", caption: "Message Date", ...MESSAGE_DATE },
    { dataField: "StopNo", caption: "Stop No", ...INT },
    { dataField: "Area", caption: "Area" },
    { dataField: "City", caption: "City" },
    { dataField: "State", caption: "State" },
    { dataField: "Driver", caption: "Driver" },
    { dataField: "Truck", caption: "Truck" },
    { dataField: "MessageCode", caption: "MessageCode" },
    { dataField: "Category", caption: "Category" },
    { dataField: "TripStatus", caption: "Trip Status", lookup: TRIP_STATUS_OPTIONS },

    { dataField: "Comment", caption: "Comment", visible: false },

    // ---- the rest of uFMM0042's grid columns ----
    { dataField: "DoStatus", caption: "DO Status", lookup: DISPATCH_STATUS_OPTIONS, visible: false },
    { dataField: "DoDate", caption: "DO Date", visible: false },
    { dataField: "TripDate", caption: "Trip Date", visible: false },
    { dataField: "MessageTypeDesc", caption: "Desc - Message Type", visible: false },
    { dataField: "UserDefinedCode1", caption: "Code -1 FMMessages", visible: false },
    { dataField: "UserDefinedCode2", caption: "Code -2 FMMessages", visible: false },
    { dataField: "UserDefinedCode3", caption: "Code -3 FMMessages", visible: false },
    { dataField: "UserDefinedCode4", caption: "Code -4 FMMessages", visible: false },
    { dataField: "UserDefinedCode5", caption: "Code -5 FMMessages", visible: false },
    { dataField: "UserDefinedReference1", caption: "Reference-1 FMMessages", visible: false },
    { dataField: "UserDefinedReference2", caption: "Reference-2 FMMessages", visible: false },
    { dataField: "UserDefinedReference3", caption: "Reference-3 FMMessages", visible: false },
    { dataField: "UserDefinedReference4", caption: "Reference-4 FMMessages", visible: false },
    { dataField: "UserDefinedReference5", caption: "Reference-5 FMMessages", visible: false },
    { dataField: "UserDefinedDate1", caption: "Date-1 FMMessages", ...DATE, visible: false },
    { dataField: "UserDefinedDate2", caption: "Date-2 FMMessages", ...DATE, visible: false },
    { dataField: "UserDefinedDate3", caption: "Date-3 FMMessages", ...DATE, visible: false },
    { dataField: "UserDefinedDate4", caption: "Date-4 FMMessages", ...DATE, visible: false },
    { dataField: "UserDefinedDate5", caption: "Date-5 FMMessages", ...DATE, visible: false },
    { dataField: "UserDefinedQuantity1", caption: "Quantity-1 FMMessages", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity2", caption: "Quantity-2 FMMessages", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity3", caption: "Quantity-3 FMMessages", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity4", caption: "Quantity-4 FMMessages", ...DEC, visible: false },
    { dataField: "UserDefinedQuantity5", caption: "Quantity-5 FMMessages", ...DEC, visible: false },
    { dataField: "LastUpdateUserId", caption: "Last Updated By", visible: false },
    { dataField: "LastUpdateDateTime", caption: "Last Updated", ...DATE, visible: false },
    { dataField: "CreationUserId", caption: "Created By", visible: false },
    { dataField: "CreationDateTime", caption: "Creation Date", ...DATE, visible: false },
    { dataField: "DoGroup", caption: "DO Group", visible: false },
    { dataField: "DoStage", caption: "DO Stage", visible: false },
    { dataField: "TruckReference", caption: "Truck Ref", visible: false },
    { dataField: "DriverReference", caption: "Driver Ref", visible: false },
    { dataField: "TrailerReference", caption: "Trailer Ref", visible: false },
    { dataField: "Trailer1Status", caption: "Trailer Status", lookup: TRAILER_STATUS_OPTIONS, visible: false },
    { dataField: "Trailer2Status", caption: "Trailer2 Status", lookup: TRAILER_STATUS_OPTIONS, visible: false },
    { dataField: "Trailer3Status", caption: "Trailer 3 Status", lookup: TRAILER_STATUS_OPTIONS, visible: false },
    { dataField: "Eta", caption: "ETA", visible: false },
    { dataField: "EdiMessageReason", caption: "EDI Message Reason", visible: false },
    { dataField: "ExceptionQty", caption: "Exception Qty", ...DEC, visible: false },
    { dataField: "ExceptionQualifier", caption: "Exception Qualifier", visible: false },
    { dataField: "ExceptionDesc", caption: "Exception Desc", visible: false },
    { dataField: "FollowupCode", caption: "Followup Code", visible: false },
    { dataField: "OsdCode", caption: "OSD Code", visible: false },
    { dataField: "FollowupReferenceDo", caption: "Followup Ref DO", visible: false },
    { dataField: "FollowupReferenceFb", caption: "Followup Ref FB", visible: false },
    { dataField: "FollowupReferenceMsg", caption: "Followup Ref Msg", visible: false },

    // uFMM0042 types these TAbIntegerField - whole degrees, not the decimal LATITUDEF / LONGITUDEF
    // pair beside them on the table. uFMM0040 types the same-named columns TAbFloatField on bottom
    // Drivers, so the two tabs legitimately format them differently; each follows its own module.
    { dataField: "Latitude", caption: "Latitude", ...INT, visible: false },
    { dataField: "Longitude", caption: "Longitude", ...INT, visible: false },
];
